import React, { useEffect, useState } from "react";
import { Coins, Megaphone, ShoppingBag, TrendingUp, UserPlus, Wallet } from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import Cabecalho from "../comum/Cabecalho";
import Carregando from "../comum/Carregando";
import FiltroPeriodo from "../comum/FiltroPeriodo";
import GraficoDias from "../comum/GraficoDias";
import Kpi from "../comum/Kpi";
import SemanaHora from "../comum/SemanaHora";
import { pct, reais } from "../comum/formato";
import { useFiltros } from "../comum/useFiltros";
import { ROTA_META_ADS } from "../rotas";
import AnunciosDaCampanha from "./AnunciosDaCampanha";
import { STATUS_CAMPANHA, carregarCampanhaMeta, lucroPorReal, retornoX } from "./api";
import Funil from "./Funil";
import PedidosDaCampanha from "./PedidosDaCampanha";
import ProdutosDaCampanha from "./ProdutosDaCampanha";

// Relatórios → Anúncios Meta → uma campanha: anúncios, produtos vendidos, pedidos e clientes.
export default function CampanhaMetaDetalhePage() {
  const { chave } = useParams();
  const { filtros, mudar, query, paraApi } = useFiltros();
  const [rel, setRel] = useState(null);
  // Erro ao carregar: aviso com "Tentar de novo" no lugar do carregamento sem fim.
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const k = JSON.stringify({ chave, de: paraApi.date_from, ate: paraApi.date_to });

  useEffect(() => {
    let vivo = true;
    // Trocar período/filtro mantém o relatório na tela até o novo chegar (25/10/2026)
    setErro(false);
    carregarCampanhaMeta(chave, { date_from: paraApi.date_from, date_to: paraApi.date_to })
      .then((d) => { if (vivo) setRel(d); })
      .catch(() => { if (vivo) { setErro(true); toast.error("Não foi possível carregar a campanha"); } });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k, tentativa]);

  const r = rel?.resumo || {};
  const c = rel?.campanha || {};
  const [statusRotulo, statusCor] = STATUS_CAMPANHA[c.status] || [c.status || "", "text-muted-foreground"];
  const cl = rel?.clientes || {};
  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-5" data-e2e="campanha-meta-detalhe">
        <Cabecalho voltarPara={ROTA_META_ADS + query()} voltarRotulo="Anúncios Meta" icone={Megaphone} corIcone="bg-sky-500/10 text-sky-500"
          titulo={!rel && !erro ? <span className="inline-block h-7 w-48 max-w-full align-middle animate-pulse rounded-md bg-primary/10" aria-label="Carregando" /> : c.nome || "Campanha"} subtitulo={rel && <span className={statusCor}>{statusRotulo}{c.meta_campaign_id ? ` · ${c.meta_campaign_id}` : ""}</span>} />
        <FiltroPeriodo de={filtros.de} ate={filtros.ate} onMudar={mudar} cor="bg-sky-500 text-white" />
        {!rel ? <Carregando erro={erro} onTentar={() => setTentativa((t) => t + 1)} /> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi rotulo="Investido" valor={reais(r.investido)} dica={`gasto ${reais(r.gasto)} + ${rel.imposto_pct}% imposto`} icone={Wallet} />
              <Kpi rotulo="Pedidos" valor={r.pedidos} dica={r.cancelados ? `${r.cancelados} cancelado(s) fora` : null} icone={ShoppingBag} e2e="kpi-campanha-pedidos" />
              <Kpi rotulo="Faturado" valor={reais(r.faturado)} dica={`ticket ${reais(r.ticket_medio)}`} icone={Coins} destaque="bg-sky-500/10 border-sky-500/30" />
              <Kpi rotulo="ROI" valor={retornoX(r.retorno)} dica="faturado ÷ investido" icone={TrendingUp} />
              <Kpi rotulo="Custo por pedido" valor={r.custo_por_pedido == null ? "—" : reais(r.custo_por_pedido)} dica={`${r.cliques} cliques · ${r.visitas} visitas`} icone={Megaphone} />
              <Kpi rotulo="Lucro bruto" valor={r.lucro_bruto == null ? "—" : reais(r.lucro_bruto)} dica={r.lucro_bruto == null ? "sem custo nos itens" : `custo em ${pct(r.cobertura_custo_pct)} dos pedidos`} icone={TrendingUp} />
              <Kpi rotulo="Lucro depois do anúncio" valor={r.lucro_depois_anuncio == null ? "—" : reais(r.lucro_depois_anuncio)} dica={lucroPorReal(r.lucro_por_real_investido)} icone={TrendingUp} destaque="bg-emerald-500/10 border-emerald-500/30" />
              <Kpi rotulo="Clientes" valor={`${cl.novos ?? 0} novos · ${cl.recorrentes ?? 0} de volta`} dica={cl.novos ? `ticket novos ${reais(cl.ticket_novos)}` : null} icone={UserPlus} />
            </div>
            <div className="grid lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3">
                <GraficoDias serie={rel.por_dia} principal="faturado" secundaria="investido" titulo="Faturado × investido por dia"
                  legenda={[{ rotulo: "Faturado", cor: "bg-sky-500" }, { rotulo: "Investido", cor: "bg-amber-400" }]} />
              </div>
              <div className="lg:col-span-2"><Funil etapas={rel.funil} /></div>
            </div>
            <AnunciosDaCampanha anuncios={rel.anuncios} />
            <ProdutosDaCampanha produtos={rel.produtos} />
            <SemanaHora semana={rel.semana} hora={rel.hora} titulo="Quando os pedidos do anúncio chegam" />
            <PedidosDaCampanha pedidos={rel.pedidos} />
          </>
        )}
      </div>
    </div>
  );
}
